import { useMemo, useRef, useState } from "react";
import { enumRulePairLogicCondition, QRulePair } from "@coreapp/CoreApp/QRulePair";
import { QHighlight } from "@coreapp/CoreApp/QHighlight";
import { useHighlightController } from "./useHighlightController";
import {
  enumChartRuleModifiers,
  useChartRuleEditorController,
  type HeaderCustomAction
} from "../Utils/useChartRuleEditorController";
import { RulesEditor } from "../Utils/RulesEditor";
import { ColorPickerControl, Flex, Text } from "@theorgchartdev/storybook";
import { getLocalizedColorPickerStrings } from "@/Utils/Utils";
import { appstr } from "@coreapp/utils/appstr";
import { ShareFilterModal } from "@/appComponents/Blocks/Modals/ShareFilterModal/ShareFilterModal";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import type { SharedFilterDoc } from "../FilterPanel/useSharedFilterController";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

type FilterBlockEditorProps = {
  highLightCtrl: ReturnType<typeof useHighlightController>;
  sharedFilterDoc?: SharedFilterDoc | null;
  onStopEditingShared?: () => void;
};

export const HighlightsEditor = (props: FilterBlockEditorProps) => {
  const { highLightCtrl, sharedFilterDoc: sharedFilterDocProp, onStopEditingShared } = props;
  const editingHighlight = highLightCtrl.highlightEditorState as QHighlight;
  const { canShareFilters: canShare } = usePermissionCheck();
  const [showShareModal, setShowShareModal] = useState(false);
  const triggerValidationRef = useRef<() => boolean>(() => true);
  const editingHighlightUID = editingHighlight.GetUID();
  const isEditingSharedHighlight = !!sharedFilterDocProp;
  const canShareCurrentHighlight = useMemo(
    () =>
      isEditingSharedHighlight ||
      highLightCtrl.sortedFilteredHighlights.some(
        highlight => highlight.GetUID() === editingHighlightUID
      ),
    [editingHighlightUID, highLightCtrl.sortedFilteredHighlights, isEditingSharedHighlight]
  );

  const headerCustomAction = useMemo<HeaderCustomAction | undefined>(
    () =>
      canShare && canShareCurrentHighlight
        ? {
            icon: "share-nodes",
            onClick: () => {
              if (triggerValidationRef.current()) setShowShareModal(true);
            },
            buttonProps: {
              modifier: "tertiary",
              tooltipProps: { message: appstr("Share highlight") },
              dataTestId: chartModeTestId("highlight", "editor-share-btn")
            }
          }
        : undefined,
    [canShare, canShareCurrentHighlight]
  );

  const ctrl = sharedFilterDocProp
    ? {
        ...highLightCtrl,
        handleStopEditing: onStopEditingShared ?? highLightCtrl.handleStopEditing
      }
    : highLightCtrl;

  const editorCtrl = useChartRuleEditorController({
    entity: enumChartRuleModifiers.Highlight,
    editingElement: editingHighlight,
    enableBlock: (highlight: QHighlight) => highlight.SetHighlightApplied(true),
    getRules: (highlight: QHighlight) => highlight.GetHighlightRule()?.GetRulePairList() ?? [],
    ctrl,
    sharedFilterDoc: sharedFilterDocProp ?? undefined,
    headerCustomAction,
    onDelete: () => highLightCtrl.handleRemoveHighlight(editingHighlight)
  });
  triggerValidationRef.current = editorCtrl.triggerValidation;
  const { blockAction, setRuleEditStatus } = editorCtrl;

  // Block edit actions
  const changeHighlightColor = blockAction((highlight: QHighlight, color: string) =>
    highlight.SetHighlightColor(color)
  );

  const addRule = blockAction(
    (
      updatingHighlight: QHighlight,
      connectsWithCondition: string,
      ruleCondition: string,
      index?: number
    ) => {
      const newRule = updatingHighlight.GetHighlightRule()?.AddBlankRulePair(index) as QRulePair;
      newRule.fCondition = ruleCondition;
      newRule.fUID = updatingHighlight.GetHighlightRule()?.GetUIDAndIncrement() || 1;

      const currentRules = updatingHighlight.GetHighlightRule()?.GetRulePairList() || [];
      const ruleIndex = index ?? currentRules.length - 1;
      const prevRule = currentRules[ruleIndex - 1];
      if (prevRule) {
        prevRule.fCondition = connectsWithCondition;
      }

      setRuleEditStatus(prevStatus => {
        const newStatus = { ...prevStatus };
        newStatus[newRule.fUID] = true;
        return newStatus;
      });
    }
  );

  const removeRule = blockAction((updatingBlock: QHighlight, ruleIndex: number) => {
    const rules = updatingBlock.GetHighlightRule()?.GetRulePairList() || [];
    const oldRuleCondition = rules?.[ruleIndex].fCondition;
    const prevRule = rules?.[ruleIndex - 1];

    if (
      prevRule?.fCondition === enumRulePairLogicCondition.or &&
      oldRuleCondition === enumRulePairLogicCondition.and
    ) {
      prevRule.fCondition = enumRulePairLogicCondition.and;
    }

    updatingBlock.GetHighlightRule()?.RemoveIndexRulePair(ruleIndex);
    if (updatingBlock.GetHighlightRule()?.GetNumRulePairs() === 0) {
      updatingBlock.GetHighlightRule()?.AddBlankRulePair();
    }
  });

  const changeRule = blockAction(
    (updatingBlock: QHighlight, ruleIndex: number, rule: QRulePair) => {
      const rules = updatingBlock.GetHighlightRule()?.GetRulePairList() || [];
      if (!rules[ruleIndex]) return;

      rules[ruleIndex] = rule;
    }
  );

  const clearAllRules = blockAction((updatingBlock: QHighlight) => {
    updatingBlock.GetHighlightRule()?.RemoveAllRulePairs();
    const newRule = updatingBlock.GetHighlightRule()?.AddBlankRulePair() as QRulePair;
    setRuleEditStatus({
      [newRule.fUID]: true
    });
  });

  const colorPicker = (
    <Flex
      justifyContent="flex-start"
      gap="8px"
      className="ocn-right-sidebar-highlight-color-picker"
    >
      <Text size="small" modifier="bold">
        {appstr("Choose a color")}:
      </Text>
      <ColorPickerControl
        currentColor={editingHighlight.GetHighlightColor() || ""}
        swatchShape={"circle"}
        swatchSize={"small"}
        onChange={changeHighlightColor}
        textTags={getLocalizedColorPickerStrings()}
      />
    </Flex>
  );

  return (
    <>
      <RulesEditor
        {...editorCtrl}
        methods={{ clearAllRules, addRule, changeRule, removeRule }}
        extra={colorPicker}
      />
      {canShare && canShareCurrentHighlight && (
        <ShareFilterModal
          show={showShareModal}
          onClose={() => setShowShareModal(false)}
          filterType="highlight"
          definition={editingHighlight.OutputToJSON?.() || {}}
          filterName={editingHighlight.GetName?.() || ""}
          filterRef={editingHighlight}
          {...(sharedFilterDocProp
            ? {
                editMode: true,
                existingFilterId: sharedFilterDocProp._id,
                existingSharedWith: sharedFilterDocProp.sharedWith
              }
            : {})}
        />
      )}
    </>
  );
};
